/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { FhirPredefinedWidget } from '../../../../algorithm/types';
import {
  IDiagnosticReportSimplified,
  IObservationSimplified,
  IObservationWithPearlCode,
  ObservationValueInterpretationEnum,
} from '../../../../Fhir/fhir_types';
import {
  fhirDateRangeValuesMap,
  getObservationValueWithInterpretation,
  toSuperscript,
} from '../../../../Fhir/fhir_utils';
import { getCodeableConceptText } from '../../../../Fhir/codeableConcept';
import { formatDate, toTitleCase } from '../../../../utils';
import { RuleEngineData } from './patientSummaryRuleEngine';
import { ObservationDetails } from './types';

export const DEFAULT_LOOKBACK_PERIOD_DAYS = 365;
export const LINE_INTENDATION_PREFIX = '<b style="font-size: 15px;">•</b> ';

// Add values that have no meaning in the lowercase
export const OBSERVATION_VALUES_WITH_NO_MEANING = new Set(['', 'comment', 'comments', 'see comment', 'see comments']);

export const getPanelName = (widget: FhirPredefinedWidget | undefined) => {
  let panelName = widget?.title ?? widget?.name ?? '';
  return removeTextInsideBrackets(panelName);
};

export function getObservationLabels(observation: IObservationWithPearlCode): ObservationDetails {
  const [observationValue, interpretation] = getObservationValueWithInterpretation({ observation });
  const observationName = getCodeableConceptText(observation?.code) || (observation as any)?.display;
  const observationUnit =
    observation?.valueQuantity?.unit
      ?.replaceAll('m2', 'm²')
      ?.replaceAll(/\bx?10[*^E]([0-9][0-9]*)/g, (_: any, d: string) => `${toSuperscript(d)}`) ?? '';
  const observationInterpretation = getObservationInterpretationLabel(interpretation);
  const observationNotes = getObservationNotes(observation);

  return {
    value: observationValue,
    name: observationName,
    unit: observationUnit,
    interpretation: observationInterpretation,
    notes: observationNotes,
  };
}

export const getObservationInterpretationLabel = (interpretation: ObservationValueInterpretationEnum | null) => {
  switch (interpretation) {
    case ObservationValueInterpretationEnum.HIGH:
      return 'High';
    case ObservationValueInterpretationEnum.LOW:
      return 'Low';
    case ObservationValueInterpretationEnum.ABNORMAL:
      return 'Abnormal';
    default:
      return '';
  }
};

export const getObservationLine = (observationDetails: ObservationDetails) => {
  let { name, value, unit, interpretation, notes } = observationDetails;
  if (value === null || value === undefined) {
    value = '';
  }
  let notesStr = '';
  if (notes && notes?.trim().length > 0) {
    // if there are notes and meaning-less observation value (or no value)
    // add the notes to the line
    if (OBSERVATION_VALUES_WITH_NO_MEANING.has(value?.trim()?.toLowerCase())) notesStr = ` <br/>${notes.trim()}`;
    else notesStr = ` (See Note)`; // if the value has meaning, add `(See Note)` label
  }
  const valueStr =
    ' ' +
    (interpretation
      ? `<b style="color:red">${value?.replaceAll('\n', '<br/>')}</b>`
      : value?.replaceAll('\n', '<br/>'));
  const interpretationStr =
    interpretation && interpretation !== 'Abnormal' ? `, <b style="color:red">${interpretation}</b>` : '';

  if (value?.trim() === '' && !notesStr) return '';

  let unitStr = unit ? `&thinsp;${unit}` : '';

  return `${LINE_INTENDATION_PREFIX}${name},${valueStr}${unitStr}${interpretationStr}${notesStr}`;
};

export const getReportDate = (diagnosticReport: IDiagnosticReportSimplified) => {
  return (
    diagnosticReport?.effectiveDateTime ||
    (diagnosticReport.result?.[0] as IObservationSimplified | undefined)?.effectiveDateTime
  );
};

export const getReportDateString = (diagnosticReport: IDiagnosticReportSimplified) => {
  return formatDate(getReportDate(diagnosticReport));
};

export function createSummarySectionHeaders({
  data,
  done,
  notDone,
}: {
  data: RuleEngineData;
  done: string[];
  notDone: string[];
}) {
  let { dateRange } = data;

  // if no date range is set (ideally it won't happen), default to the configured lookback period
  if (dateRange !== 0 && !dateRange) {
    dateRange = DEFAULT_LOOKBACK_PERIOD_DAYS;
  }
  const isAll = dateRange === 0;

  const label = toTitleCase(`${!isAll ? 'Past ' : ''}${fhirDateRangeValuesMap[dateRange]}`);

  done.push(`<b><u>Done (${label}):</u></b>`);
  notDone.push(`<b><u>Not Done (${label}):</u></b>`);
}

export function addSummaryPlaceholders(notDone: string[], done: string[], lifetimeDone: string[]): void {
  // It will always have the header, hence comparing with 1
  if (notDone.length === 1) {
    notDone.push('No data available');
  }
  if (done.length === 1) {
    done.push('No data available');
  }
  // Add a line space if there are any lifetime done observations
  if (lifetimeDone.length > 0) {
    lifetimeDone.push('');
  }
}

export function compileObservationSummary({
  widget,
  observations,
  lifetimeDone,
  done,
  notDone,
  doneTitle,
  panelName,
}: {
  widget: FhirPredefinedWidget | undefined;
  observations: string[];
  lifetimeDone: string[];
  done: string[];
  notDone: string[];
  doneTitle: string;
  panelName: string;
}) {
  if (observations.length > 0) {
    // If the report is valid for the lifetime
    if (widget?.lookbackTimeMonths === -1) {
      // Add lifetime done section only if there are any lifetime done observations
      if (lifetimeDone.length === 0) {
        lifetimeDone.push('<b><u>Done (Lifetime Lookback):</u></b>');
      }
      lifetimeDone.push(doneTitle);
      lifetimeDone.push(...observations);
      lifetimeDone.push('');
    } else {
      done.push(doneTitle);
      done.push(...observations);
      done.push('');
    }
  } else {
    notDone.push(`${LINE_INTENDATION_PREFIX}${panelName}`);
  }
}

const getObservationNotes = (observation: IObservationWithPearlCode) => {
  return observation?.note?.map((note, i) => `<b>Note:</b><pre>${note.text}</pre>`)?.join('<br/>');
};

const removeTextInsideBrackets = (text: string) => text.replace(/\(.*?\)/g, '').trim();
